/**
 * filasPertenecenAlDocumento.ts — P4IDENT (18-sep-2026)
 *
 * Guarda por identidad de documento: `processedData.infrastructures` (lo que
 * cotejan P3 y exporta P4) solo puede contener filas cuyo `id` pertenezca al
 * documento activo (`extractionData.infrastructures`). El `id` nace en el
 * extractor (`documentExtractor.ts`, `inf_<ts>_<aleatorio>`) y sobrevive a
 * normalización, geocodificación y edición manual (todas hacen `...infra` y
 * fusionan por `id`), así que es la identidad fiable; el municipio no lo es
 * (`P4GUARD` midió 912 discrepancias legítimas).
 *
 * Cierra OBS-P4-EXPORT-SIN-GUARDA-MUNICIPIO-FILAS-VS-CABECERA. Es defensa en
 * profundidad: la causa del estado mezclado la corrigió CAMDOC.
 *
 * Sin documento activo (`extraction` nulo) no hay con qué comparar y la guarda
 * NO bloquea: es el caso de la restauración desde caché, que repone
 * `processedData` pero no `extractionData`.
 */

export interface FilaConId {
  id: string
}

export type MotivoPertenencia = 'sin-documento' | 'todas' | 'parcial' | 'ninguna'

export interface VeredictoPertenencia {
  /** `true` si P3/P4 pueden usar las filas tal cual. */
  pertenecen: boolean
  motivo: MotivoPertenencia
  /** Filas de `processed` cuyo `id` no está en el documento activo. */
  ajenas: number
  total: number
}

export function filasPertenecenAlDocumento(
  processed: readonly FilaConId[] | null | undefined,
  extraction: readonly FilaConId[] | null | undefined,
): VeredictoPertenencia {
  const filas = processed ?? []
  const total = filas.length

  if (!extraction) {
    return { pertenecen: true, motivo: 'sin-documento', ajenas: 0, total }
  }

  const idsDocumento = new Set(extraction.map(f => f.id))
  const ajenas = filas.reduce((n, f) => (idsDocumento.has(f.id) ? n : n + 1), 0)

  if (ajenas === 0) {
    return { pertenecen: true, motivo: 'todas', ajenas, total }
  }
  return {
    pertenecen: false,
    motivo: ajenas === total ? 'ninguna' : 'parcial',
    ajenas,
    total,
  }
}

/** Texto del aviso, sellado por Luis (RAE) en P4IDENT. */
export function mensajeFilasAjenas(fichero: string | undefined): string {
  const nombre = fichero && fichero.trim() ? fichero : 'sin nombre'
  return `Las infraestructuras cargadas no pertenecen al documento activo (${nombre}). Vuelve a Normaliza y carga de nuevo el documento antes de exportar.`
}
